import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  feePromotionCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { feeEngineEndpoints } from "@/features/fee-engine/api/endpoints";
import type { PromotionDto } from "@/features/fee-engine/api/dto";

/**
 * Désactive une promotion (`POST /Promotions/{id}/disable`, guide §6.5).
 * Les remises déjà accordées ne sont pas remises en cause.
 */
export const useDisablePromotion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["fee-engine", "promotions", "disable"],
    mutationFn: (promotionId: string) =>
      mutateData({
        url: feeEngineEndpoints.promotions.disable(promotionId),
        method: "POST",
      }) as Promise<PromotionDto>,
    onSuccess: (_data, promotionId) => {
      return invalidateAfterMutation(
        queryClient,
        feePromotionCacheKeys(promotionId)
      );
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useDisablePromotion failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "fee-engine" }
      );
    },
  });
};
